import { openDB, IDBPDatabase } from 'idb';

export interface TaleFyiNativeAlignment {
  paras: Array<[string | number, number]>;
  phraseBegins: number[];
  phraseIndexes: number[];
  title?: string;
  author?: string;
  language?: string;
  total_duration_seconds?: number;
}

export interface SavedTestAlignSession {
  id: string;
  title: string;
  author: string;
  language: string;
  htmlContent: string;
  alignment: TaleFyiNativeAlignment;
  audioBlob?: Blob;
  audioSrc?: string;
  audioFileName?: string;
  createdAt: number;
  savedTime?: number;
  mappingOffset?: number;
  diagnosticSummary?: string;
}

export type SavedTestAlignMeta = Omit<SavedTestAlignSession, 'audioBlob' | 'htmlContent' | 'alignment'> & {
  parasCount: number;
  phrasesCount: number;
};

const DB_NAME = 'libritui_test_align_db';
const STORE_NAME = 'test_align_sessions';
const DB_VERSION = 1;
const HTML_CACHE_NAME = 'libritui_test_align_html_v1';

// Cache API Helpers para armazenamento robusto de documentos HTML grandes (~1MB)
async function saveHtmlToCache(id: string, html: string): Promise<boolean> {
  if (typeof window === 'undefined' || !('caches' in window)) return false;
  try {
    const cache = await caches.open(HTML_CACHE_NAME);
    const response = new Response(html, {
      headers: { 
        'Content-Type': 'text/html; charset=utf-8',
        'X-Created-At': String(Date.now())
      }
    });
    await cache.put(`/test-align-html/${encodeURIComponent(id)}`, response);
    return true;
  } catch (err) {
    console.warn('[testAlignStorage] Cache API save error:', err);
    return false;
  }
}

async function getHtmlFromCache(id: string): Promise<string | null> {
  if (typeof window === 'undefined' || !('caches' in window)) return null;
  try {
    const cache = await caches.open(HTML_CACHE_NAME);
    const response = await cache.match(`/test-align-html/${encodeURIComponent(id)}`);
    if (response) {
      return await response.text();
    }
    return null;
  } catch (err) {
    console.warn('[testAlignStorage] Cache API read error:', err);
    return null;
  }
}

async function deleteHtmlFromCache(id: string): Promise<void> {
  if (typeof window === 'undefined' || !('caches' in window)) return;
  try {
    const cache = await caches.open(HTML_CACHE_NAME);
    await cache.delete(`/test-align-html/${encodeURIComponent(id)}`);
  } catch {}
}

class TestAlignStorageService {
  private db: IDBPDatabase<any> | null = null;
  private isConnecting: Promise<IDBPDatabase<any>> | null = null;

  /**
   * Obtém uma conexão ativa com o IndexedDB.
   * Se a conexão anterior foi fechada ou estiver em estado de fechamento, reabre uma nova conexão limpa.
   */
  private async getDb(): Promise<IDBPDatabase<any>> {
    if (typeof window === 'undefined') {
      throw new Error('IndexedDB indisponível no servidor.');
    }

    if (this.db) {
      try {
        const testTx = this.db.transaction(STORE_NAME, 'readonly');
        testTx.abort();
        return this.db;
      } catch {
        console.warn('[testAlignStorage] Conexão IDB anterior fechada ou inválida. Reabrindo...');
        try { this.db.close(); } catch {}
        this.db = null;
      }
    }

    if (this.isConnecting) {
      return this.isConnecting;
    }

    this.isConnecting = (async () => {
      try {
        const db = await openDB(DB_NAME, DB_VERSION, {
          upgrade(dbInstance) {
            if (!dbInstance.objectStoreNames.contains(STORE_NAME)) {
              const store = dbInstance.createObjectStore(STORE_NAME, { keyPath: 'id' });
              store.createIndex('by_createdAt', 'createdAt');
            }
          },
          blocked() {
            console.warn('[testAlignStorage] Conexão IDB bloqueada.');
          },
          blocking() {
            console.warn('[testAlignStorage] Conexão IDB bloqueando outra versão.');
          },
          terminated() {
            console.warn('[testAlignStorage] Conexão IDB terminada.');
          }
        });

        (db as any).onclose = () => {
          this.db = null;
        };

        this.db = db;
        return db;
      } finally {
        this.isConnecting = null;
      }
    })();

    return this.isConnecting;
  }

  async getAllSessions(): Promise<SavedTestAlignMeta[]> {
    try {
      const db = await this.getDb();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const all: SavedTestAlignSession[] = await tx.store.getAll();
      await tx.done;

      return all
        .map((item) => ({
          id: item.id,
          title: item.title,
          author: item.author,
          language: item.language,
          audioFileName: item.audioFileName,
          createdAt: item.createdAt,
          savedTime: item.savedTime || 0,
          parasCount: item.alignment?.paras?.length || 0,
          phrasesCount: item.alignment?.phraseBegins?.length || 0,
        }))
        .sort((a, b) => b.createdAt - a.createdAt);
    } catch (err) {
      console.warn('[testAlignStorage] Erro ao listar sessões:', err);
      return [];
    }
  }

  async getSession(id: string): Promise<SavedTestAlignSession | null> {
    try {
      const db = await this.getDb();
      const tx = db.transaction(STORE_NAME, 'readonly');
      const record: SavedTestAlignSession | undefined = await tx.store.get(id);
      await tx.done;

      if (!record) return null;

      // Recuperar o documento HTML da Cache API se estiver salvo lá
      const cachedHtml = await getHtmlFromCache(id);
      if (cachedHtml) {
        record.htmlContent = cachedHtml;
      }

      return record;
    } catch (err) {
      console.warn('[testAlignStorage] Erro ao carregar sessão, tentando reconectar...', err);
      try {
        this.db = null;
        const db = await this.getDb();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const record: SavedTestAlignSession | undefined = await tx.store.get(id);
        await tx.done;
        if (!record) return null;
        const cachedHtml = await getHtmlFromCache(id);
        if (cachedHtml) {
          record.htmlContent = cachedHtml;
        }
        return record;
      } catch {
        return null;
      }
    }
  }

  async saveSession(session: Omit<SavedTestAlignSession, 'createdAt'> & { createdAt?: number }): Promise<SavedTestAlignSession> {
    const createdAt = session.createdAt || Date.now();
    const savedTime = session.savedTime || 0;
    const originalHtml = session.htmlContent || '';

    // 1. Armazenar o documento HTML (~1MB) na Cache API (sem restrição de timeout ou transação)
    const savedInCache = await saveHtmlToCache(session.id, originalHtml);

    // 2. Registro para o IndexedDB
    // Se o HTML foi gravado com sucesso na Cache API, guardamos string vazia no IndexedDB
    // para não sobrecarregar a transação IDB nem arriscar timeout de serialização
    const record: SavedTestAlignSession = {
      ...session,
      htmlContent: savedInCache ? '' : originalHtml,
      createdAt,
      savedTime,
    };

    // 3. Execução explícita da transação com modo 'readwrite' e await tx.done
    try {
      const db = await this.getDb();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      await Promise.all([
        tx.store.put(record),
        tx.done
      ]);
    } catch (err: any) {
      console.warn('[testAlignStorage] Transação IDB falhou na primeira tentativa. Reabrindo conexão e retentando...', err);
      // Forçar reset da conexão
      try { this.db?.close(); } catch {}
      this.db = null;

      const db = await this.getDb();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      await Promise.all([
        tx.store.put(record),
        tx.done
      ]);
    }

    return {
      ...record,
      htmlContent: originalHtml
    };
  }

  async deleteSession(id: string): Promise<void> {
    try {
      await deleteHtmlFromCache(id);
      const db = await this.getDb();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      await Promise.all([
        tx.store.delete(id),
        tx.done
      ]);
    } catch (err) {
      console.warn('[testAlignStorage] Erro ao deletar sessão:', err);
    }
  }

  async updateProgress(id: string, time: number): Promise<void> {
    try {
      const db = await this.getDb();
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const record = await tx.store.get(id);
      if (record) {
        record.savedTime = time;
        await tx.store.put(record);
      }
      await tx.done;
    } catch {}
  }
}

export const testAlignStorage = new TestAlignStorageService();
